import React from "react";
import { connect } from "react-redux";
import { Input, Select } from "../../Input";
import { Button } from "../../Button";
import language from "../../../language";
import axios from "axios";
import toastMessage from "../../../utils/toastMessage";
import { getStorage } from "../../../utils/storage";
import { Empty } from "../../Empty";
import moment from "moment";
import TESTForm from "../TESTForm";
import bloodGroups from "../../../constants/bloodGroups";
import { fetchBloodTestQuestionnaire } from "../../../utils/queries/bloodTest/questionnaireQuery";
import accessMenu from "../../../utils/accessMenu";
import { handleValidateMatching } from "../utils";

const API_URL = process.env.REACT_APP_BBMS_BASE_API;

const DISPLAY_LABEL = {
  "Liss": "Liss/Incubation at 37°C",
};

const FIELD_GUIDANCE = {
  "Rhesus (Rh D)": "Positive if D antigen is present. Negative requires Liss test.",
  "Liss": "Enabled when Rhesus is Negative. Positive → donor is D+; Negative → proceed to IAT.",
  "Indirect Antiglobulin Test - IAT (Du+/d-)": "Enabled when Liss is Negative. Du+ = weak D antigen detected; d- = absent.",
  "Kell (K+/K-)": "K+ if Kell antigen is present, K- if absent.",
  "Antibody screening": "Detects unexpected antibodies in the donor's plasma.",
  "RHC (C+/C-)": "C+ if Rh C antigen is present, C- if absent.",
  "HRC (c+/c-)": "c+ if Rh c antigen is present, c- if absent.",
  "HRE (e+/e-)": "e+ if Rh e antigen is present, e- if absent.",
};

const CC_QUESTION_TEXT = "Check Cells (CC)";
const CC_OPTIONS = [
  { label: "Positive", value: "Positive" },
  { label: "Negative", value: "Negative" },
];
const CC_SETTINGS = {
  Positive: { abbreviation: "CC" },
  Negative: { abbreviation: "CC" },
};

const ABOList = [
  {
    label: "A",
    value: "A",
  },
  {
    label: "B",
    value: "B",
  },
  {
    label: "AB",
    value: "AB",
  },
  {
    label: "O",
    value: "O",
  },
];

class BloodGrouping extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSubmitting: false,
      currentTime: moment().format("lll"),
      donation: {},
      questionnaire: [],
      forms: {},
      error: {},
      disabled: true,
      showForm: true,
      confirmationModal: false,
      activateDonation: true,
      code: "",
      canConfirm: false,
      mismatchInfo: {},
      previousTestResults: props.previousTestResults || [],
      misMatchFields: [],
      checkCells: null,
      checkCellsError: null,
    };
  }

  setStateAsync = (state) =>
    new Promise((resolve) => this.setState(state, resolve));

  getIatAnswer() {
    const iatQ = this.state.questionnaire.find(
      (q) => q.questionText === "Indirect Antiglobulin Test - IAT (Du+/d-)"
    );
    return (iatQ?.answer?.value || iatQ?.answer || "").trim();
  }

  // Questionnaires may use "d-"/"Du+" or "Negative"/"Positive" as option values
  iatIsNegative() {
    const val = this.getIatAnswer();
    return val === "d-" || /^negat/i.test(val);
  }

  iatIsPositive() {
    const val = this.getIatAnswer();
    return val === "Du+" || /^posit/i.test(val);
  }

  normalizeQualysAnswer = (value, answerType) => {
    if (value === undefined || value === null || value === "") {
      return null;
    }

    const normalizedValue =
      typeof value === "string"
        ? value.toUpperCase() === "POS"
          ? "Positive"
          : value.toUpperCase() === "NEG"
            ? "Negative"
            : value
        : value;

    return answerType === "multipleChoice"
      ? {
          label: normalizedValue,
          value: normalizedValue,
        }
      : normalizedValue;
  };

  getSampleBloodGroup = (sampleData = {}) => {
    const aboGroup = sampleData?.aboGroup;
    const rh = sampleData?.rh;

    if (!aboGroup) return null;

    if (!rh) return aboGroup;

    const normalizedRh =
      typeof rh === "string" ? rh.toUpperCase() : rh?.value?.toUpperCase();

    return `${aboGroup}${normalizedRh === "POS" || normalizedRh === "POSITIVE" ? "+" : ""}`;
  };

  applySampleDataToQuestionnaire = async (questionnaireData = []) => {
    const { sampleData } = this.props;

    if (!sampleData) return;

    const preparedQuestionnaire = questionnaireData.map((question) => ({
      ...question,
      answer: null,
    }));

    const sampleBloodGroup = this.getSampleBloodGroup(sampleData);
    const sampleAboGroup = sampleData?.aboGroup;
    const selectedABO =
      ABOList.find((el) => el.value === sampleAboGroup) || null;

    await this.setStateAsync({
      questionnaire: preparedQuestionnaire,
      formQuestionnaire: questionnaireData,
      aboBloodGroup: selectedABO,
      bloodGroup: sampleBloodGroup
        ? {
            label: sampleBloodGroup,
            value: sampleBloodGroup,
          }
        : null,
    });

    for (let index = 0; index < preparedQuestionnaire.length; index += 1) {
      const question = preparedQuestionnaire[index];

      if (!question.qualys) continue;

      const answer = this.normalizeQualysAnswer(
        sampleData[question.qualys],
        question.answerType,
      );

      if (answer === null) continue;

      await this.onChangeQuestionnaire({
        value: answer,
        index,
      });
    }
  };

  componentDidMount = async () => {
    const {
      canCreateBloodTestBloodGrouping,
      canConfirmBloodTestBloodGrouping,
      canUpdateBloodTestBloodGrouping,
    } = accessMenu();

    // Only count non-cancelled records for code generation
    // so a cancelled test doesn't create a gap in the sequence
    const activeResults = (this.state.previousTestResults || []).filter(
      (r) => r.status !== "cancelled"
    );
    this.setState({
      code: "BT00" + (activeResults.length + 1),
    });

    await this.getQuestionnaires(true);

    this.setState({ canConfirm: canConfirmBloodTestBloodGrouping });

    if (
      canCreateBloodTestBloodGrouping ||
      canConfirmBloodTestBloodGrouping ||
      canUpdateBloodTestBloodGrouping
    ) {
      this.setState({
        disabled: false,
      });
    } else {
      return this.setState({
        showForm: false,
        disabled: true,
      });
    }

    if (this.props._id || this.props.sampleData) {
      const selectedBloodGroup =
        this.props.bloodGroup ||
        this.getSampleBloodGroup(this.props.sampleData);

      const aboOnly = selectedBloodGroup?.replace(/[+-].*/, "") || "";
      this.setState({
        ...this.props,
        bloodGroup: selectedBloodGroup
          ? {
              label: selectedBloodGroup,
              value: selectedBloodGroup,
            }
          : null,
        aboBloodGroup: aboOnly
          ? {
              label: aboOnly,
              value: aboOnly,
            }
          : null,
      });
      if (this.props.questionnaire?.length > 0) {
        const modifiedQuestions = this.props.questionnaire
          .filter((el) => el.questionText !== CC_QUESTION_TEXT)
          .map((el) => {
            const q = this.props.questionnaire.find(
              (qEl) => qEl.questionText === el.questionText,
            );
            if (q) {
              el["answer"] = q.answer;
            }
            return el;
          });
        this.setState({ questionnaire: modifiedQuestions });

        const savedCC = this.props.questionnaire.find(
          (q) => q.questionText === CC_QUESTION_TEXT
        );
        if (savedCC?.answer) {
          const ccVal = typeof savedCC.answer === "object" ? savedCC.answer : { label: savedCC.answer, value: savedCC.answer };
          this.setState({ checkCells: ccVal });
        }

        const iatQ = this.props.questionnaire.find(
          (q) => q.questionText === "Indirect Antiglobulin Test - IAT (Du+/d-)"
        );
        const iatAnswer = iatQ?.answer?.value || iatQ?.answer;
        if (iatAnswer === "Du+") {
          const bgBase = (aboOnly || "O") + "+";
          this.formateBloodGroup(bgBase, "Du+", "+");
        }
      }
    }
  };

  getQuestionnaires = async (isLoading) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoading });

      let { data } = await fetchBloodTestQuestionnaire(defaultLanguage, {
        page: 1,
        limit: 100,
        type: "bloodGrouping",
      });

      if (this.props.sampleData) {
        await this.applySampleDataToQuestionnaire(data);
      }

      this.setState({
        formQuestionnaire: data,
        isLoading: false,
      });

      if (!this.props.sampleData) {
        this.setState({
          questionnaire: data,
        });
      }
    } catch (error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  onChangeQuestionnaire = async ({ value, index }) => {
// eslint-disable-next-line no-unused-vars
    let { questionnaire, error } = this.state;

    let question = questionnaire[index];

    let answerValue = value?.target ? value?.target?.value : value;

    questionnaire[index]["answer"] = answerValue;

    delete questionnaire[index]["error"];

    await this.setStateAsync({
      questionnaire,
    });

    const answerValueValue = answerValue?.value || answerValue;

    handleValidateMatching({
      that: this,
      field: questionnaire[index]?.questionText,
      value: answerValueValue,
      questionnaireIndex: index,
    });

    if (
      question?.settings &&
      question?.settings?.[answerValueValue] &&
      Object.keys(question?.settings?.[answerValueValue])?.includes(
        "activateDonation",
      )
    ) {
      this.setState({
        activateDonation:
          question?.settings?.[answerValueValue]?.activateDonation,
      });
    }

    console.log(
      "this.state.aboBloodGroup?.value",
      this.state.aboBloodGroup?.value,
      value,
    );

    if (question.questionText === "Rhesus (Rh D)") {
      if (value?.value?.toUpperCase() === "POSITIVE") {
        this.formateBloodGroup(
          this.state.aboBloodGroup?.value,
          value?.value || value,
        );
      } else {
        let g = this.state.aboBloodGroup?.value?.replace(/\+/g, "");
        this.formateBloodGroup(g, value?.value || value);
      }

      if (
        this.state?.bloodGroup?.value?.includes("+") &&
        value?.value?.toUpperCase() === "NEGATIVE"
      ) {
        questionnaire[index]["error"] =
          "Invalid Rhesus for blood group " + this.state?.bloodGroup?.value;
      }

      if (
        this.state?.bloodGroup?.value?.includes("-") &&
        value?.value?.toUpperCase() === "POSITIVE"
      ) {
        questionnaire[index]["error"] =
          "Invalid Rhesus for blood group " + this.state?.bloodGroup?.value;
      }

      questionnaire.forEach((el, i) => {
        if (
          value?.value?.toUpperCase() === "NEGATIVE" &&
          el?.questionText.toUpperCase() === "LISS"
        ) {
          el.required = true;
        } else {
          el.required = false;
          delete el.error;

          this.formateBloodGroup(
            this.state.aboBloodGroup?.value,
            value?.value || value,
          );
        }

        if (
          (value?.value?.toUpperCase() === "POSITIVE" &&
            el?.questionText.toUpperCase() === "LISS") ||
          el?.questionText === "Indirect Antiglobulin Test - IAT (Du+/d-)"
        ) {
          el.answer = null;
        }
      });

      // IAT hides when Rhesus is not Negative → CC is no longer relevant
      if (value?.value?.toUpperCase() !== "NEGATIVE") {
        this.setState({ checkCells: null, checkCellsError: null });
      }
    }

    if (question.questionText.toUpperCase() === "LISS") {
      questionnaire.forEach((el) => {
        if (
          value?.value?.toUpperCase() === "NEGATIVE" &&
          el?.questionText === "Indirect Antiglobulin Test - IAT (Du+/d-)"
        ) {
          el.required = true;
        } else {
          el.required = false;
          delete el.error;
        }

        if (
          value?.value?.toUpperCase() !== "NEGATIVE" &&
          el?.questionText === "Indirect Antiglobulin Test - IAT (Du+/d-)"
        ) {
          el.answer = null;
        }

        if (value?.value?.toUpperCase() === "POSITIVE") {
          const bloodGroup = this.state.aboBloodGroup?.value.replace(/\+/g, "");
          this.formateBloodGroup(bloodGroup, value?.value || value, "+");
        } else {
          if (this.state.aboBloodGroup?.value.includes("+")) {
            const bloodGroup = this.state.aboBloodGroup?.value.replace(
              /\+/g,
              "",
            );

            this.formateBloodGroup(bloodGroup, value?.value || value);
          } else if (this.state.aboBloodGroup?.value.includes("+")) {
            const bloodGroup = this.state.aboBloodGroup?.value.replace(
              /\+/g,
              "",
            );

            this.formateBloodGroup(bloodGroup, value?.value || value);
          }
        }
      });

      // IAT hides when Liss is not Negative → CC is no longer relevant
      if (value?.value?.toUpperCase() !== "NEGATIVE") {
        this.setState({ checkCells: null, checkCellsError: null });
      }
    }

    if (question.questionText === "Indirect Antiglobulin Test - IAT (Du+/d-)") {
      if (value?.value?.trim() === "Du+" || /^posit/i.test(value?.value?.trim() || "")) {
        let g = this.state.aboBloodGroup?.value.replace(/-/g, "") + "+";
        this.formateBloodGroup(g, value?.value || value, "+");
        // Du+/Positive means weak D present — CC only applies to d-/Negative result
        this.setState({ checkCells: null, checkCellsError: null });
      } else {
        // IAT = d-/Negative: do NOT apply "-" yet — wait for CC = Positive to confirm
        let g = this.state.aboBloodGroup?.value.replace(/[+-]/g, "");
        this.formateBloodGroup(g, "Negative");
      }
    }

    await this.setStateAsync({ questionnaire });

    handleValidateMatching({
      that: this,
      field: "bloodGroup",
      value: this.state.bloodGroup,
      questionnaireIndex: index,
    });
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState?.bloodGroup?.value !== this.state.bloodGroup?.value ||
      prevState?.aboBloodGroup?.value !== this.state.aboBloodGroup?.value
    ) {
      const value = this.state?.bloodGroup?.value;
      let questionnaire = this.state.questionnaire;

      // handleValidateMatching({ that: this, field: "bloodGroup", value });

      for (let el of questionnaire) {
        if (el.questionText === "Rhesus (Rh D)") {
          if (
            value?.value?.includes("-") &&
            el?.answer?.value?.toUpperCase() === "POSITIVE"
          ) {
            el["error"] = "Invalid Rhesus for blood group " + value;
          } else {
            delete el.error;
          }

          if (
            value?.value?.includes("+") &&
            el?.answer?.value?.toUpperCase() === "NEGATIVE"
          ) {
            el["error"] = "Invalid Rhesus for blood group " + value?.value;
          } else {
            delete el.error;
          }
        }
      }

      this.setState({ questionnaire });

      handleValidateMatching({
        that: this,
        field: "bloodGroup",
        value: this.state.bloodGroup,
      });
    }
  }

  formateBloodGroup(bloodGroup, rhesus, signFrom) {
    let { aboBloodGroup } = this.state;

    console.log({ bloodGroup, rhesus, signFrom, aboBloodGroup });

    if (typeof bloodGroup !== "string") return;

    if (bloodGroup) {
      let splitB = bloodGroup?.includes("+")
        ? bloodGroup?.split("+")
        : bloodGroup?.includes("-")
          ? bloodGroup?.split("-")
          : [bloodGroup];
      if (splitB.length > 0) {
        const selectedABO = ABOList.find((el) => el.value === splitB[0]);

        aboBloodGroup = selectedABO;
      }
    }

    let bloodGroupValue = aboBloodGroup;

    if (aboBloodGroup) {
      let sign = "";

      if (rhesus) {
        sign = rhesus?.toUpperCase() === "POSITIVE" ? "+" : "";
      }

      if (signFrom) {
        sign = signFrom;
      }

      const duSuffix = rhesus?.trim() === "Du+" ? " Du+" : "";
      const baseGroup = {
        label: aboBloodGroup.label + sign,
        value: aboBloodGroup.label + sign,
      };

      aboBloodGroup = baseGroup;
      bloodGroupValue = duSuffix
        ? { label: baseGroup.label + duSuffix, value: baseGroup.value + duSuffix }
        : baseGroup;
    }

    this.setState(
      {
        aboBloodGroup,
        bloodGroup: bloodGroupValue,
      },
      () => {
        handleValidateMatching({
          that: this,
          field: "bloodGroup",
          value: this.state.bloodGroup,
        });
      },
    );
  }

  onChangeText(field, e) {
    let { error, questionnaire } = this.state;

    delete error[field];

    const value = e && e?.target ? e?.target?.value : e;

    this.setState({
      [field]: value,
      error,
    });

    if (field === "aboBloodGroup") {
      this.setState({ bloodGroup: value });
      questionnaire.forEach((el) => {
        el.answer = null;
      });

      if (field === "aboBloodGroup") {
        questionnaire.forEach((el, i) => {
          if (el.questionText === "Rhesus (Rh D)" && el.answer) {
            this.formateBloodGroup(value.value, el.answer?.value || el.answer);
          }
        });
      }
    }

    this.setState(
      {
        questionnaire,
      },
      () => {
        handleValidateMatching({
          that: this,
          field,
          value: this.state[field],
        });

        handleValidateMatching({
          that: this,
          field: "bloodGroup",
          value: this.state.bloodGroup,
        });
      },
    );
  }

  validateForm() {
    let { questionnaire, error, code, bloodGroup, formQuestionnaire } =
      this.state;

    let isRequiredIds = [];

    formQuestionnaire.forEach((el) => {
      if (el.required && isRequiredIds.indexOf(el._id) === -1) {
        isRequiredIds.push(el._id);
      }
    });

    questionnaire.forEach((el) => {
      if (isRequiredIds.includes(el._id) && (!el.answer || el.answer === "")) {
        el.error = `${el.questionText} ${
          language[this.props.defaultLanguage].is_required
        }`;
      }
    });

    if (code === "") {
      error.code = language[this.props.defaultLanguage].code_required;
    }

    if (!bloodGroup) {
      error.bloodGroup =
        language[this.props.defaultLanguage].blood_group_required;
    }

    console.log(error);

    this.setState({ questionnaire, error });
  }

  onSubmit = async (status) => {
    const { questionnaire, bloodGroup, error, code } = this.state;

    const { testId } = this.props;

    // CC validation — must be answered when IAT = d-/Negative
    const iatAnswer = this.getIatAnswer();
    const iatNeg = this.iatIsNegative();
    if (iatNeg && !this.state.checkCells) {
      this.setState({ checkCellsError: "Check Cells is required when IAT is Negative (d-)" });
      return;
    }

    await this.validateForm();

    let questionnaireError = questionnaire
      .map((el) => {
        if (el.error) {
          return el.error;
        }
        return null;
      })
      .filter((error) => error !== undefined && error !== null);

    if (Object.keys(error).length > 0) return;

    if (questionnaireError.length > 0) return;

    this.setState({
      isSubmitting: true,
    });

    await this.handleUpdateQuestionnaire();

    let url = `${API_URL}/bloodTestHistory`,
      method = "POST",
      user = await getStorage();
    let { _id } = this.props;

    // Build final questionnaire: remove any stale CC entry then re-inject current result
    let finalQuestionnaire = questionnaire.filter((q) => q.questionText !== CC_QUESTION_TEXT);
    if (iatNeg && this.state.checkCells) {
      finalQuestionnaire.push({
        questionText: CC_QUESTION_TEXT,
        answer: this.state.checkCells,
        answerType: "multipleChoice",
        options: ["Positive", "Negative"],
        printingEnabled: true,
        settings: CC_SETTINGS,
      });
    }

    let requestedBody = {
      testBy: user.id,
      bloodTest: testId,
      questionnaireType: "bloodGrouping",
      questionnaire: finalQuestionnaire,
      code,
      bloodGroup: bloodGroup?.value,
    };

    if (_id && _id !== "") {
      method = "PUT";
      requestedBody.id = _id;

      requestedBody["updatedBy"] = requestedBody.testBy;

      delete requestedBody.testBy;
    }

    if (status) {
      requestedBody.status = status;
    }

    const options = {
      method,
      url,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + user.token,
      },
      data: requestedBody,
    };

    axios(options)
      .then(async (data) => {
        this.setState({
          isSubmitting: false,
        });

        if (this.props.canConfirm) {
          console.log("Should confirm the blood test");
          await this.handleValidation();
        }

        toastMessage(
          "success",
          language[this.props.defaultLanguage][
            method === "PUT"
              ? "update_bloodtest_success"
              : "add_bloodtest_success"
          ],
        );

        this.props.getData && this.props.getData();
        this.props.handleCloseModal && this.props.handleCloseModal();
        this.props.onResetForm && this.props.onResetForm();
      })
      .catch((error) => {
        this.setState({ isSubmitting: false });
        toastMessage("error", error);
      });
  };

  handleOpenModal(modal) {
    this.setState({
      [modal]: true,
    });
  }

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  handleValidation = async () => {
    try {
      const { questionnaire, code, error, bloodGroup } = this.state;

      await this.validateForm();

      if (Object.keys(error).length > 0) return;

      this.setState({ isValidating: true });

      const { testId } = this.props;

      const user = await getStorage();

      let requestedBody = {
        bloodTestId: testId,
        type: "bloodGrouping",
        questionnaire,
        code,
        bloodGroup: bloodGroup?.value,
      };

      console.log("requestedBody", requestedBody);

      const options = {
        method: "POST",
        url: `${API_URL}/bloodTest/comfirmTest`,
        data: requestedBody,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + user.token,
        },
      };

      axios(options).then((data) => {
        this.setState({
          isValidating: false,
        });

        toastMessage(
          "success",
          language[this.props.defaultLanguage].confirm_blood_test,
        );
        this.props.getData && this.props.getData();
        this.props.handleCloseModal && this.props.handleCloseModal();
        this.props.onResetForm && this.props.onResetForm();
      });
    } catch (error) {
      this.setState({
        isValidating: false,
      });

      toastMessage(
        "error",
        language[this.props.defaultLanguage].confirm_blood_test_not_same,
      );
    }
  };

  renderChangePrevEntry({ field, questionnaireIndex, type, options }) {
    if (this.state.previousTestResults.length > 0) {
      return this.state.previousTestResults?.map((el, i) => {
        return this.renderChangePrevEntryInput({
          label: el.code,
          field,
          questionnaireIndex,
          type,
          options,
          testIndex: i,
        });
      });
    }
  }

  renderChangePrevEntryInput({
    type,
    options,
    field,
    testIndex,
    questionnaireIndex,
    label,
  }) {
    let value;

    if (questionnaireIndex !== undefined && testIndex !== undefined) {
      value =
        this.props?.previousTestResults[testIndex].questionnaire[
          questionnaireIndex
        ].answer;
    } else if (testIndex !== undefined) {
      value = this.props?.previousTestResults[testIndex][field];
    }

    if (type === "dropdown") {
      return (
        <Select
          key={testIndex}
          options={options}
          label={label + "-ENTRY-" + field}
          value={
            typeof value === "string"
              ? options.map((el) => el.value === value && el)
              : value
          }
          onChange={(e) =>
            this.handleChangePrevEntry({
              field,
              index: testIndex,
              value: e && e?.target ? e?.target?.value : e,
              questionnaireIndex,
            })
          }
          disabled
        />
      );
    }

    return (
      <Input
        label={label + "-ENTRY-" + field}
        value={value}
        onChange={(e) =>
          this.handleChangePrevEntry({
            field,
            index: testIndex,
            value: e && e?.target ? e?.target?.value : e,
            questionnaireIndex,
          })
        }
      />
    );
  }

  isQuestionDisabled(form) {
    const { questionnaire } = this.state;

    const rhesusQ = questionnaire.find((q) => q.questionText === "Rhesus (Rh D)");
    const rhesusVal = (rhesusQ?.answer?.value || rhesusQ?.answer || "").toUpperCase();

    const lissQ = questionnaire.find((q) => q.questionText?.toUpperCase() === "LISS");
    const lissVal = (lissQ?.answer?.value || lissQ?.answer || "").toUpperCase();

    const qt = form.questionText?.toUpperCase();

    if (qt === "LISS") {
      return rhesusVal !== "NEGATIVE";
    }

    if (form.questionText === "Indirect Antiglobulin Test - IAT (Du+/d-)") {
      return rhesusVal !== "NEGATIVE" || lissVal !== "NEGATIVE";
    }

    return false;
  }

  handleChangePrevEntry({ index, field, questionnaireIndex, value }) {
    // if (field === "Rhesus (RHD R D)") {
    //   this.formateBloodGroup(
    //     this.state.aboBloodGroup?.value,
    //     value?.value || value
    //   );
    // }

    const previousTestResults = [...this.state.previousTestResults];
    if (questionnaireIndex !== undefined) {
      previousTestResults[index]["questionnaire"][questionnaireIndex]["answer"] = value;

      if (previousTestResults[index]["questionnaire"][questionnaireIndex]["answer"] === value) {
        const questionnaire = [...(this.state.questionnaire || [])];
        delete questionnaire[questionnaireIndex]["error"];
        this.setState({ questionnaire });
      }

      this.setState({ previousTestResults }, () => {
        handleValidateMatching({
          that: this,
          field,
          value: value?.value || value,
          questionnaireIndex,
        });
      });
    } else {
      previousTestResults[index][field] = value;

      this.setState({ previousTestResults }, () => {
        handleValidateMatching({ that: this, field, value: this.state[field] });
      });
    }
  }

  handleUpdateQuestionnaire = async () => {
    for (let el of this.state.previousTestResults) {
      try {
        let url = `${API_URL}/bloodTestHistory`,
          user = await getStorage();

        let requestedBody = {
          bloodGroup: el.bloodGroup?.value || el.bloodGroup,
          questionnaire: el.questionnaire,
          id: el._id,
          bloodTest: el.bloodTest._id,
        };

        delete requestedBody._id;

        return await axios({
          url,
          method: "PUT",
          data: requestedBody,
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  render() {
    if (!this.state.showForm)
      return (
        <Empty
          title={language[this.props.defaultLanguage].no_access}
          icon="help"
          description={
            language[this.props.defaultLanguage].no_access_description
          }
        />
      );

    // console.log(this.state.error);

    return (
      <div>
        <div className="card-body">
          {this.state.misMatchFields.length > 0 && (
            <div className="alert alert-danger">
              <div
                style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
              >
                {language[this.props.defaultLanguage].mismatch}:
                {this.state.misMatchFields.join(",")}
              </div>
            </div>
          )}
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="mb-3">
              <div className="mb-2">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <Input
                        label={language[this.props.defaultLanguage].code}
                        placeholder={
                          language[this.props.defaultLanguage].code_placeholder
                        }
                        value={this.state?.code}
                        error={this.state.error.code}
                        onChange={(e) => this.onChangeText("code", e)}
                      />
                      <Select
                        options={ABOList}
                        label={language[this.props.defaultLanguage].blood_group}
                        value={this.state?.aboBloodGroup}
                        error={this.state.error.aboBloodGroup}
                        onChange={(e) => this.onChangeText("aboBloodGroup", e)}
                      />
                      {/* {this.state.aboBloodGroup && (
                        <Select
                          options={bloodGroups}
                          label={
                            language[this.props.defaultLanguage].blood_group
                          }
                          value={this.state?.bloodGroup}
                          error={this.state.error.bloodGroup}
                          onChange={(e) => this.onChangeText("bloodGroup", e)}
                          disabled
                        />
                      )} */}
                      {this.state.misMatchFields.includes("bloodGroup") && (
                        <>
                          {this.renderChangePrevEntry({
                            field: "bloodGroup",
                            type: "dropdown",
                            options: bloodGroups,
                          })}
                        </>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div
                        className=""
                        style={{
                          height: 200,
                          alignItems: "center",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <h1 style={{ fontSize: 60, margin: 0 }} className="text-primary">
                          {(() => {
                            const val = this.state?.bloodGroup?.value || "";
                            const duIdx = val.indexOf(" Du+");
                            if (duIdx === -1) return val;
                            return (
                              <>
                                {val.slice(0, duIdx)}
                                <span style={{ fontSize: 18, verticalAlign: "middle" }}>
                                  {" Du+"}
                                </span>
                              </>
                            );
                          })()}
                        </h1>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <div className="card-body">
                <div className="row">
                  {this.state.questionnaire.map((form, index) => {
                    const options = form?.options?.map((el) => {
                      return {
                        label: el,
                        value: el,
                      };
                    });
                    const isIAT = form.questionText === "Indirect Antiglobulin Test - IAT (Du+/d-)";
                    return (
                      <React.Fragment key={index}>
                        <div className="col-md-4">
                          <TESTForm
                            label={DISPLAY_LABEL[form.questionText] || form.questionText}
                            error={form.error}
                            {...form}
                            options={options}
                            onChange={(e) =>
                              this.onChangeQuestionnaire({
                                index,
                                form,
                                value: e,
                              })
                            }
                            disabled={this.state.disabled || this.isQuestionDisabled(form)}
                            helperText={FIELD_GUIDANCE[form.questionText]}
                          />
                          {this.state.misMatchFields.includes(
                            form.questionText,
                          ) &&
                            this.renderChangePrevEntry({
                              field: form.questionText,
                              questionnaireIndex: index,
                              type:
                                form.answerType === "multipleChoice"
                                  ? "dropdown"
                                  : "",
                              options: options,
                            })}
                        </div>
                        {/* Check Cells always rendered right after IAT, disabled until IAT = Negative */}
                        {isIAT && (
                          <div className="col-md-4">
                            <TESTForm
                              questionText={CC_QUESTION_TEXT}
                              answerType="multipleChoice"
                              options={CC_OPTIONS}
                              answer={this.state.checkCells}
                              error={this.state.checkCellsError}
                              onChange={(e) => {
                                this.setState({ checkCells: e, checkCellsError: null });
                                const base = (this.state.aboBloodGroup?.value || "").replace(/[+-]/g, "");
                                if (e?.value === "Positive") {
                                  this.formateBloodGroup(base, "Negative", "-");
                                } else {
                                  this.formateBloodGroup(base, "Negative");
                                }
                              }}
                              disabled={this.state.disabled || !this.iatIsNegative()}
                              helperText="Positive confirms d- (weak D absent). Negative = IAT invalid, must be repeated — label will show IAT: Missing."
                              required={this.iatIsNegative()}
                            />
                            {this.state.checkCells?.value === "Negative" && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#856404",
                                  backgroundColor: "#fff3cd",
                                  border: "1px solid #ffc107",
                                  padding: "4px 8px",
                                  borderRadius: 4,
                                  marginTop: 4,
                                }}
                              >
                                IAT result is <strong>invalid</strong>. Please repeat the IAT test. This donation will show <strong>IAT: Missing</strong> on the label.
                              </div>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Button
                text={language[this.props.defaultLanguage].cancel}
                onPress={() => this.props.handleCloseModal()}
                className="btn-default"
              />
              <Button
                text={language[this.props.defaultLanguage].submit}
                onPress={() => this.onSubmit()}
                isSubmitting={this.state.isSubmitting}
                disabled={this.state.questionnaire.length === 0}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(BloodGrouping);
